import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import logoImage from "@/assets/zavvi-logo.png";

const PoliticaPrivacidade = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="py-4 px-4 border-b border-border bg-card/50">
        <div className="container mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={logoImage} alt="Zavvi.ai" className="h-8 object-contain" />
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={16} />
            Voltar ao site
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-2">
          Política de Privacidade
        </h1>
        <p className="text-muted-foreground text-sm mb-8">
          Data de disponibilização: 14/01/2026 | Última atualização: 14/01/2026
        </p>

        <div className="prose prose-invert max-w-none space-y-8 text-body-text">
          <p className="text-sm leading-relaxed">
            Olá, Titular!
          </p>
          
          <p className="text-sm leading-relaxed">
            A <strong>GF SERVICOS ONLINE</strong>, inscrita no CNPJ sob o nº 55.287.952/0001-2, tem um compromisso sério com a privacidade e a proteção dos dados pessoais das pessoas impactadas pelas suas atividades. Por isso, criamos este Aviso de Privacidade (o "Aviso"), para informar você como coletamos, utilizamos, processamos e compartilhamos, quando estritamente necessário, dados pessoais quando você utiliza a Plataforma Zavvi.
          </p>

          <p className="text-sm leading-relaxed">
            A Plataforma Zavvi é um Software as a Service (SaaS) que permite aos seus Usuários a otimização da comunicação com leads e clientes, através de uma plataforma de multiatendimento de gestão de atividades e mensagens via WhatsApp e Instagram, durante o período contratado no Plano de Assinatura adquirido, de acordo com os <Link to="/termos" className="text-cyber-cyan hover:underline">Termos e Condições de Uso da Zavvi</Link>.
          </p>

          <p className="text-sm leading-relaxed">
            Para que nossa relação seja transparente e adequada à Lei Geral de Proteção de Dados Pessoais (a "LGPD"), criamos este Aviso através do qual informaremos a você como coletamos, utilizamos, processamos e divulgamos suas informações e dados pessoais.
          </p>

          <div className="bg-cyber-cyan/10 border border-cyber-cyan/20 rounded-lg p-4">
            <p className="text-sm text-foreground">
              <strong>Importante:</strong> Nós coletamos o mínimo possível de Dados Pessoais na prestação dos nossos serviços e optamos por não ter acesso a nenhum Dado Pessoal a que não precisamos ter, efetivamente, acesso, para cumprimento de nossas atividades.
            </p>
          </div>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">ÍNDICE</h2>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>Conceitos Importantes</li>
              <li>A Quem se Aplica este Aviso?</li>
              <li>Papéis e Responsabilidades no Tratamento de Dados</li>
              <li>Como São Tratados os Dados Pessoais</li>
              <li>Compartilhamento de Dados Pessoais</li>
              <li>Como Protegemos Seus Dados</li>
              <li>Seus Direitos como Titular</li>
              <li>Por Quanto Tempo Armazenamos Seus Dados</li>
              <li>Disposições Gerais</li>
              <li>Canal de Comunicação</li>
            </ol>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">1. CONCEITOS IMPORTANTES</h2>
            <p className="text-sm leading-relaxed mb-4">
              Alguns conceitos para auxiliar na compreensão deste Aviso:
            </p>
            <ul className="space-y-3 text-sm">
              <li><strong>Anonimização:</strong> Processo pelo qual os dados pessoais coletados se tornam arquivos impossíveis de identificar a pessoa a quem se referem.</li>
              <li><strong>ANPD:</strong> Autoridade Nacional de Proteção de Dados, órgão responsável por fiscalizar o cumprimento da LGPD.</li>
              <li><strong>Cliente:</strong> Pessoa que efetua o pagamento da assinatura da Plataforma Zavvi.</li>
              <li><strong>Controlador:</strong> O responsável pelas decisões referentes ao tratamento de dados dos titulares.</li>
              <li><strong>Dados Pessoais:</strong> Informações que identificam uma pessoa natural, como e-mail, CPF, telefone e endereço.</li>
              <li><strong>Dados Pessoais Sensíveis:</strong> Dado pessoal sobre origem racial, convicção religiosa, dado referente à saúde, dado genético ou biométrico.</li>
              <li><strong>LGPD:</strong> Lei Geral de Proteção de Dados Pessoais, Lei nº 13.709/2018.</li>
              <li><strong>Operador:</strong> Quem realiza o tratamento de dados segundo as orientações do Controlador.</li>
              <li><strong>Plataforma Zavvi:</strong> Software as a Service (SaaS) de multiatendimento via WhatsApp e Instagram.</li>
              <li><strong>Tratamento de Dados:</strong> Toda operação realizada com dados pessoais.</li>
              <li><strong>Titular:</strong> Pessoa física a quem se referem os dados pessoais.</li>
              <li><strong>Usuário:</strong> Pessoa cadastrada para utilização da Plataforma Zavvi.</li>
            </ul>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">2. A QUEM SE APLICA ESTE AVISO?</h2>
            <p className="text-sm leading-relaxed">
              Este Aviso de Privacidade se aplica aos Clientes, Usuários da Plataforma Zavvi e visitantes do nosso site.
            </p>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">3. PAPÉIS E RESPONSABILIDADES</h2>
            <p className="text-sm leading-relaxed mb-4">
              A <strong>GF SERVICOS ONLINE</strong>, inscrita no CNPJ sob o nº 55.287.952/0001-2, é a responsável pelo tratamento dos dados pessoais dos Usuários da Plataforma Zavvi e do Site, agindo como Controladora desses dados.
            </p>
            <p className="text-sm leading-relaxed mb-4">
              Todavia, quando um Cliente da Zavvi usa a plataforma, ele também age como <strong>Controlador Conjunto</strong> sobre esses dados, sendo indispensável que mantenha suas próprias políticas e medidas técnicas e administrativas de controle e proteção de dados pessoais.
            </p>
            <div className="bg-electric-purple/10 border border-electric-purple/20 rounded-lg p-4">
              <p className="text-sm text-foreground">
                <strong>Importante:</strong> A Zavvi não é e nem será responsável pelo Tratamento de Dados realizado por terceiros, dentro ou fora do seu Software as a Service.
              </p>
            </div>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">4. COMO SÃO TRATADOS OS DADOS PESSOAIS</h2>
            
            <h3 className="text-lg font-semibold text-foreground mb-3">4.1. Dados coletados para acesso à Plataforma:</h3>
            <ul className="list-disc list-inside space-y-1 text-sm mb-4">
              <li>Nome</li>
              <li>Telefone</li>
              <li>E-mail</li>
              <li>IP</li>
              <li>Senha</li>
              <li>Aceite de Termos e Condições de Uso</li>
            </ul>

            <h3 className="text-lg font-semibold text-foreground mb-3">4.2. Dados coletados para comunicação:</h3>
            <ul className="list-disc list-inside space-y-1 text-sm mb-4">
              <li>Nome</li>
              <li>Telefone</li>
              <li>E-mail</li>
            </ul>

            <h3 className="text-lg font-semibold text-foreground mb-3">4.3. Dados coletados indiretamente:</h3>
            <p className="text-sm leading-relaxed mb-4">
              A nossa empresa pode coletar indiretamente durante a utilização da Plataforma pelo Usuário: endereço IP, sessão, registros de data e horário de ações, aceite de Termos, atividades no Software as a Service, por meio de webhooks e cookies.
            </p>

            <h3 className="text-lg font-semibold text-foreground mb-3">4.4. Negativa de tratamento:</h3>
            <div className="bg-success-green/10 border border-success-green/20 rounded-lg p-4">
              <ul className="space-y-2 text-sm">
                <li>✓ A Zavvi <strong>NÃO</strong> tem acesso aos dados que os Usuários operam na plataforma.</li>
                <li>✓ A Zavvi <strong>NÃO</strong> tem acesso aos dados dos contatos telefônicos dos Usuários.</li>
                <li>✓ A Zavvi <strong>NÃO</strong> tem acesso às conversas dos Usuários.</li>
              </ul>
            </div>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">5. COMPARTILHAMENTO DE DADOS PESSOAIS</h2>
            <p className="text-sm leading-relaxed mb-4">
              A Zavvi pode compartilhar os dados necessários para que seja possível a manutenção das suas operações de forma eficiente. Dados poderão ser compartilhados com empresas contratadas para que as funcionalidades estejam disponíveis e/ou para fornecer uma melhor experiência para o Cliente e os Usuários.
            </p>
            <p className="text-sm leading-relaxed mb-4">
              Terão acesso aos dados pessoais coletados, como Operadores de dados:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm mb-4">
              <li>Plataformas de pagamento (Mercado Pago)</li>
              <li>Plataforma WhatsApp (Meta)</li>
              <li>Plataforma Instagram (Meta)</li>
              <li>Serviços de hospedagem em nuvem</li>
            </ul>
            <p className="text-sm leading-relaxed">
              Todas as nossas contratações prezam pelo tratamento seguro das informações do Cliente/Usuário. A partir do momento que esses terceiros tiverem acesso aos dados compartilhados, serão responsáveis pelo seu tratamento em segurança.
            </p>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">6. COMO PROTEGEMOS SEUS DADOS</h2>
            <p className="text-sm leading-relaxed mb-4">
              A Zavvi se compromete com a segurança de todos seus Usuários e, por isso, adota procedimentos físicos, eletrônicos e administrativos que garantem a privacidade e segurança dos seus dados, com medidas técnicas compatíveis com os padrões esperados e boas práticas de segurança da informação.
            </p>
            <ul className="space-y-2 text-sm">
              <li>✓ Sempre que possível, não coletamos dados sobre a sua utilização da plataforma.</li>
              <li>✓ Em hipótese alguma, teremos acesso às suas conversas.</li>
              <li>✓ Todas as tecnologias utilizadas respeitam a legislação vigente e os termos deste Aviso.</li>
            </ul>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">7. SEUS DIREITOS COMO TITULAR</h2>
            <p className="text-sm leading-relaxed mb-4">
              De acordo com a LGPD, você possui os seguintes direitos:
            </p>
            <ul className="space-y-3 text-sm">
              <li><strong>Acesso e confirmação:</strong> Você pode solicitar o acesso à relação completa de dados coletados e tratados.</li>
              <li><strong>Retirada de consentimento:</strong> Você poderá retirar a sua anuência com o Tratamento de Dados Pessoais a qualquer momento.</li>
              <li><strong>Correção de dados:</strong> Você pode solicitar a correção de dados que estão incompletos, inexatos ou desatualizados.</li>
              <li><strong>Anonimização, bloqueio ou eliminação:</strong> Caso acredite que algum dado está sendo tratado de forma desnecessária ou em desconformidade com a LGPD.</li>
              <li><strong>Informação sobre compartilhamento:</strong> Você pode solicitar informações sobre nossos parceiros e fornecedores.</li>
              <li><strong>Portabilidade:</strong> Você poderá solicitar a portabilidade dos seus dados pessoais a outro fornecedor.</li>
              <li><strong>Oposição:</strong> Você pode objetar o tratamento de alguns Dados Pessoais ou apresentar oposição ao tratamento por completo.</li>
              <li><strong>Registro de reclamação:</strong> Você poderá procurar a ANPD a qualquer momento.</li>
            </ul>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">8. POR QUANTO TEMPO ARMAZENAMOS SEUS DADOS?</h2>
            <p className="text-sm leading-relaxed mb-4">
              Os dados fornecidos pelo Cliente/Usuário serão armazenados e processados enquanto seu cadastro estiver ativo.
            </p>
            <p className="text-sm leading-relaxed mb-4">
              Caso o Usuário desejar, pode solicitar a exclusão da sua conta e dos seus dados pessoais através do nosso Canal de Atendimento.
            </p>
            <p className="text-sm leading-relaxed">
              Diante da solicitação de exclusão ou término da finalidade de tratamento, o Usuário terá todos os seus dados deletados permanentemente, exceto os dados cuja manutenção seja obrigatória por lei ou regulamentação.
            </p>
          </section>

          <hr className="border-border" />

          <section>
            <h2 className="text-xl font-display font-bold text-foreground mb-4">9. DISPOSIÇÕES GERAIS</h2>
            <p className="text-sm leading-relaxed mb-4">
              A qualquer momento, unilateralmente, poderemos modificar/atualizar este Aviso. Caso isso aconteça, o Titular será notificado e a versão atualizada valerá a partir de sua publicação.
            </p>
            <p className="text-sm leading-relaxed">
              A continuidade de acesso ou utilização do Cliente/Usuário após divulgação da atualização confirmará a ciência e vigência do novo Aviso.
            </p>
          </section>

          <hr className="border-border" />

          <section className="bg-muted/50 rounded-lg p-6">
            <h2 className="text-xl font-display font-bold text-foreground mb-4">10. CANAL DE COMUNICAÇÃO</h2>
            <p className="text-sm leading-relaxed mb-4">
              Nós disponibilizamos um canal de comunicação para oferecer suporte ao titular de dados pessoais e quaisquer questões relacionadas a este Aviso:
            </p>
            <ul className="space-y-2 text-sm">
              <li>
                <strong>WhatsApp:</strong>{" "}
                <a href="https://wa.me/5511936180993" className="text-cyber-cyan hover:underline" target="_blank" rel="noopener noreferrer">
                  +55 11 93618-0993
                </a>
              </li>
              <li>
                <strong>E-mail:</strong>{" "}
                <a href="mailto:contato@zavvi.ai" className="text-cyber-cyan hover:underline">
                  contato@zavvi.ai
                </a>
              </li>
            </ul>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-border bg-card/50">
        <div className="container mx-auto px-4 text-center">
          <p className="text-muted-foreground text-sm">
            © 2026 Zavvi.ai. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PoliticaPrivacidade;
